import { useMemo } from "react";
import { useParams } from "react-router";
import { Button } from "primereact/button";

import { useLevelContext } from "sj-ui-toolkit/src/atlas/contexts/Level/LevelContextProvider";
import { useEnrollments } from "sj-ui-toolkit/src/api/hooks";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { PageBreadcrumb } from "sj-ui-toolkit/src/atlas/components";
import { useSmartNavigation } from "sj-ui-toolkit/src/helpers/navigation";
import { INTERNAL_ROUTES } from "sj-ui-toolkit/src/constants/routes";

import { LearninGrid } from "../LearninGrid/LearninGrid";
import { getLevelVariant } from "../../helpers/levelVariants";

const LevelPage = () => {
  const { levelId } = useParams();
  const parsedLevelId = Number(levelId);

  const { levels, levelsLoading, levelDetails, isLoadingLevelDetails } = useLevelContext();
  const { data: currentPaths = [] } = useEnrollments();
  const { navigateTo } = useSmartNavigation();

  const level = levels.find(l => l.id === parsedLevelId);
  const variant = getLevelVariant(level);

  const paths = levelDetails[parsedLevelId];
  const pathsLoading = paths === undefined || levelsLoading || isLoadingLevelDetails;

  useBreadcrumb(
    level ? { label: level.name, route: `/levels/${parsedLevelId}`, display: "page" } : null
  );

  const isCompleted = level?.status === "passed";

  const items = useMemo(() => {
    if (!paths?.length) return [];
    // Completed levels: show every path (all are enrolled/passed) so the page isn't empty.
    if (isCompleted) return paths;

    const enrolledRoleIds = new Set(
      (currentPaths ?? []).filter(p => p.roleId != null).map(p => p.roleId)
    );
    return paths.filter(p => p?.id == null || !enrolledRoleIds.has(p.id));
  }, [paths, currentPaths, isCompleted]);

  const levelNotFound = !levelsLoading && !level;

  if (levelNotFound) {
    return (
      <div className="my-8 flex flex-col gap-4">
        <h1 className="atlas-h2">Level Not Found</h1>
        <p className="body-regular">We couldn't find the level you were looking for.</p>
        <div className="mt-2">
          <Button
            label={`Back to ${INTERNAL_ROUTES.Learn.name}`}
            onClick={() => navigateTo(INTERNAL_ROUTES.Learn.route)}
          />
        </div>
      </div>
    );
  }

  return (
    <div data-test-id="learn-level-page">
      <PageBreadcrumb />
      <LearninGrid
        items={items}
        loading={pathsLoading}
        variant={variant}
        pathType="Path"
        title={level?.name ?? null}
        description={level?.description}
        searchInputId="level-path-search"
        completed={isCompleted}
      />
    </div>
  );
};

export default LevelPage;
